import { useOrgContext } from '@provider-directory/components/org-context/org-context-provider';
import { PROVIDER_SCHEDULING_TYPES } from '@provider-directory/constants';
import { useFeatureFlags } from '@provider-directory/contexts/FlagContext';
import { getDecisionTreeSpecialty } from '@provider-directory/models/decision-tree.model';
import {
  getProviderPrimarySpecialty,
  isPrimaryCarePediatricsProvider,
} from '@provider-directory/models/provider.model';
import type { ProviderResource } from '@provider-directory/types/providers.contracts';

function isSpecialtyFFDisabled(
  provider: ProviderResource,
  featureFlags: ReturnType<typeof useFeatureFlags>['featureFlags'],
  specialtyMap: Record<string, string>,
): boolean {
  const primarySpecialty =
    getProviderPrimarySpecialty(provider)?.name.toLowerCase() ?? '';
  const mapped = specialtyMap[primarySpecialty];

  if (mapped === 'cardiology' && !featureFlags.isCardiologyDecisionTreeEnabled)
    return true;
  if (
    mapped === 'dermatology' &&
    !featureFlags.isDermatologyDecisionTreeEnabled
  )
    return true;
  if (mapped === 'allergy' && !featureFlags.isAllergyDecisionTreeEnabled)
    return true;
  if (mapped === 'orthopedics' && !featureFlags.isOrthoDecisionTreeEnabled)
    return true;
  if (
    mapped === 'endocrinology' &&
    !featureFlags.isEndocrinologyDecisionTreeEnabled
  )
    return true;
  if (
    mapped === 'gastroenterology' &&
    !featureFlags.isGastroenterologyDecisionTreeEnabled
  )
    return true;

  // Primary Care: when FF is off, force offline state for any provider that
  // would otherwise route through the Primary Care tree (V2).
  if (
    isPrimaryCarePediatricsProvider(provider, specialtyMap) &&
    !featureFlags.isPrimaryCareTreeEnabled
  ) {
    return true;
  }

  return false;
}

export type SchedulingUIMode = 'slots' | 'call' | 'call+book';
export function useSchedulingDecision(
  provider: ProviderResource,
  hasSlots: boolean,
): SchedulingUIMode {
  const { featureFlags } = useFeatureFlags();
  const {
    config: { decisionTree },
  } = useOrgContext();

  const specialtyDisabled = isSpecialtyFFDisabled(
    provider,
    featureFlags,
    decisionTree.specialtyMap,
  );

  if (!featureFlags.isSchedulingEnabled || specialtyDisabled) {
    return 'call';
  }

  if (hasSlots && provider.allowsOpenScheduling) {
    return 'slots';
  }

  // If we can't find a matching d-tree then we won't offer booking
  const dtreeSpecialty = getDecisionTreeSpecialty({
    specialtyMap: decisionTree.specialtyMap,
    provider,
    featureFlags,
  });

  if (!dtreeSpecialty) {
    return 'call';
  }

  // derive directly from provider.scheduling instead of behavior helper
  return provider.scheduling &&
    [
      PROVIDER_SCHEDULING_TYPES.OnlineFabric,
      PROVIDER_SCHEDULING_TYPES.Offline,
    ].includes(provider.scheduling)
    ? 'call+book'
    : 'call';
}
